import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItem,
  ListItemIcon,
  Typography,
  Badge,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import './PortalNavBar.scss';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import ViewQuiltRoundedIcon from '@mui/icons-material/ViewQuiltRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonIcon from '@mui/icons-material/Person';
import VrpanoRoundedIcon from '@mui/icons-material/VrpanoRounded';
import { useLocation, useNavigate } from 'react-router';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { useGetDocumentTree } from '../../../../api/documentHub.ts';
import { getUnreadDocumentCount } from '../../../../unstructured-pages/DocumentHubPage/tableUtills.tsx';
import { useGetCustomer } from '../../../../api/contact.ts';

interface AccordionMenuProps {
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const AccordionMenu: React.FC<AccordionMenuProps> = ({
  title,
  icon,
  content,
}) => {
  return (
    <Accordion className="accordion">
      <AccordionSummary id="panel-header" aria-controls="panel-content">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListItemIcon>{icon}</ListItemIcon>
          <Typography>{title}</Typography>
          <ArrowRightIcon className="arrow-icon" />
        </Box>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

interface PortalNavBarMenuItemProps {
  handleNavigate: (val: string) => void;
}

const LandMenu = ({ handleNavigate }: PortalNavBarMenuItemProps) => {
  return (
    <List id="land-menu">
      <ListItem onClick={() => handleNavigate('/search')}>Land Search</ListItem>
      <ListItem onClick={() => handleNavigate('/search/favorites')}>
        My Favorites
      </ListItem>
      <ListItem onClick={() => handleNavigate('/search/saved')}>
        Saved Searches
      </ListItem>
    </List>
  );
};

const HomeMenu = ({ handleNavigate }: PortalNavBarMenuItemProps) => {
  return (
    <List id="home-menu">
      <ListItem onClick={() => handleNavigate('/models')}>Home Search</ListItem>
      <ListItem onClick={() => handleNavigate('/search/favorites')}>
        My Favorites
      </ListItem>
    </List>
  );
};

interface MoreMenuProps extends PortalNavBarMenuItemProps {
  showBadge: boolean;
  badgeCount: number;
}

const MoreMenu = ({ handleNavigate, showBadge, badgeCount }: MoreMenuProps) => {
  return (
    <List id="more-menu">
      <ListItem onClick={() => handleNavigate('/pricing')}>
        Pricing Dashboard
      </ListItem>
      <ListItem onClick={() => handleNavigate('/portal/message-center')}>
        Message Center
      </ListItem>
      <ListItem onClick={() => handleNavigate('/portal/document-hub')}>
        Document Hub
        {showBadge && (
          <Badge
            sx={{ marginLeft: '24px' }}
            badgeContent={badgeCount}
            color="error"
          />
        )}
      </ListItem>
      <ListItem onClick={() => handleNavigate('/portal/style')}>
        Style Inspiration
      </ListItem>
      <ListItem onClick={() => handleNavigate('/contact-information')}>
        Contact Information
      </ListItem>
    </List>
  );
};

interface PortalNavBarProps {
  showNavBar: boolean;
  setShowNavBar: (showNavBar: boolean) => void;
}

export const PortalNavBar = (props: PortalNavBarProps) => {
  const { showNavBar, setShowNavBar } = props;

  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { data: userAttributes, loading } = useGetCustomer();

  const { data: folders } = useGetDocumentTree();

  const handleNavigate = (path: string) => {
    void navigate(path);
    setShowNavBar(false);
  };

  return (
    <>
      <Drawer
        open={showNavBar}
        id="portal-nav-bar"
        data-testid="portal-nav"
        className={`drawer`}
        anchor="left"
        onClose={() => setShowNavBar(false)}
        slotProps={{
          backdrop: { style: { backgroundColor: 'transparent' } },
        }}
      >
        <Box>
          <List sx={{ paddingTop: '124px' }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => handleNavigate('/portal')}
                className={`${pathname === '/portal' && 'selected'}`}
              >
                <ListItemIcon>
                  <ViewQuiltRoundedIcon className="nav-list-icon" />
                </ListItemIcon>

                <Typography>My Portal</Typography>
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <AccordionMenu
                title="My Land"
                icon={<VrpanoRoundedIcon className="nav-list-icon" />}
                content={<LandMenu handleNavigate={handleNavigate} />}
              />
            </ListItem>

            <ListItem disablePadding>
              <AccordionMenu
                title="My Home"
                icon={<HomeRoundedIcon className="nav-list-icon" />}
                content={<HomeMenu handleNavigate={handleNavigate} />}
              />
            </ListItem>

            <ListItem disablePadding>
              <AccordionMenu
                title="More"
                icon={<AddBoxRoundedIcon className="nav-list-icon" />}
                content={
                  <MoreMenu
                    handleNavigate={handleNavigate}
                    showBadge={!!folders}
                    badgeCount={
                      folders ? (getUnreadDocumentCount(folders) ?? 0) : 0
                    }
                  />
                }
              />
            </ListItem>
            {userAttributes?.sk !== 'customer' && !loading && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigate('customer-center')}
                  className={`${pathname.includes('customer') && 'selected'}`}
                >
                  <ListItemIcon>
                    <PersonIcon className="nav-list-icon" />
                  </ListItemIcon>
                  <Typography align="center">Customer Center</Typography>
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};
